import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

const REMINDER_ID = 1001;
const ROSTER_UPLOADED_KEY = 'roster-uploaded-month';

/** Check if roster has been uploaded for current month */
export function hasUploadedThisMonth(): boolean {
  const key = getCurrentMonthKey();
  return localStorage.getItem(ROSTER_UPLOADED_KEY) === key;
}

/** Mark current month as uploaded — cancels pending reminder */
export async function markRosterUploaded(): Promise<void> {
  localStorage.setItem(ROSTER_UPLOADED_KEY, getCurrentMonthKey());
  if (Capacitor.isNativePlatform()) {
    try {
      await LocalNotifications.cancel({ notifications: [{ id: REMINDER_ID }] });
    } catch { /* ignore if not scheduled */ }
  }
}

/** Schedule monthly reminder on the 1st at 9 AM local time */
export async function setupRosterReminder(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  try {
    // Request permission
    let perms = await LocalNotifications.checkPermissions();
    if (perms.display === 'prompt' || perms.display === 'prompt-with-rationale') {
      perms = await LocalNotifications.requestPermissions();
    }
    if (perms.display !== 'granted') return false;

    // Cancel existing before re-scheduling
    await LocalNotifications.cancel({ notifications: [{ id: REMINDER_ID }] });

    await LocalNotifications.schedule({
      notifications: [
        {
          id: REMINDER_ID,
          title: 'New month — add your flights',
          body: 'Add your flights for the new month to stay connected with your crew!',
          schedule: {
            on: { day: 1, hour: 9, minute: 0 },
            repeats: true,
          },
          extra: { screen: 'roster-upload' },
        },
      ],
    });

    return true;
  } catch (err) {
    console.warn('[rosterReminder] setup failed:', err);
    return false;
  }
}

/** Register the tap listener — call once on app startup */
export function registerReminderListener(
  onOpenRoster: () => void
): void {
  if (!Capacitor.isNativePlatform()) return;

  LocalNotifications.addListener(
    'localNotificationActionPerformed',
    (action) => {
      if (action.notification.extra?.screen === 'roster-upload') {
        onOpenRoster();
      }
    }
  );
}

function getCurrentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}
